import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";

import AuthService from "@/common/api/auth.service";
import InstitutionsService from "@/common/api/institutions.service";
import type { ApiError, ApiResponse, UniloginResponse } from "@/common/api/types";
import UniloginService from "@/common/api/unilogin.service";
import type { UniloginCredentials, User } from "@/common/types/auth";
import type { Institution } from "@/types/institution.type";

import { useFeatureFlagsStore, useLicenseStore, useSettingsStore } from ".";

interface State {
  user: User;
  isAuthenticated: boolean | null;
}

const initialState: State = {
  user: {
    id: null,
    primaryInstitutionId: null,
    expires: null,
    function: "",
    username: "",
    name: "",
    level: undefined,
    className: "",
    institutions: []
  },
  isAuthenticated: null
};

export const useAuthStore = defineStore("auth", () => {
  const state = reactive<State>({ ...initialState });

  const userDisplayName = computed<string>(() => state.user.username?.substring(0, 12));
  const userIsTeacher = computed<boolean>(() => state.user.function === "teacher");

  const login = async (
    credentials: UniloginCredentials
  ): Promise<ApiResponse<UniloginResponse>> => {
    const { setLicenses } = useLicenseStore();
    const { setSettings } = useSettingsStore();
    const { setFeatureFlags } = useFeatureFlagsStore();

    try {
      const response = await UniloginService.response(credentials);

      const { user, settings, licenses, featureFlags } = response.data.data;

      state.user = user;
      state.isAuthenticated = true;

      setLicenses(licenses);
      setSettings(settings);
      setFeatureFlags(featureFlags);

      return response.data;
    } catch (e: unknown) {
      const error = e as ApiError;
      throw Error(error.response?.data?.message);
    }
  };

  const logout = async (): Promise<void> => {
    await AuthService.logout();
    resetAuth();
  };

  const loadUser = async (): Promise<void> => {
    const { setLicenses } = useLicenseStore();
    const { setSettings } = useSettingsStore();
    const { setFeatureFlags } = useFeatureFlagsStore();

    try {
      const response = await AuthService.user();
      const { user, licenses, settings, featureFlags } = response.data.data;

      state.user = user;
      state.isAuthenticated = true;

      setLicenses(licenses);
      setSettings(settings);
      setFeatureFlags(featureFlags);
    } catch (e: unknown) {
      const error = e as ApiError;
      throw Error(error.response?.data?.message);
    }
  };

  const resetAuth = (): void => {
    const { setActiveLanguageGroup } = useSettingsStore();

    Object.assign(state, { ...initialState });
    state.isAuthenticated = false;

    setActiveLanguageGroup(undefined);
  };

  const isLoaded = computed<boolean>(() => state.isAuthenticated !== null);

  const getPrimaryInstitutionName = (): string => {
    const primaryInstitution = state.user.institutions.find(
      (institution) => institution.id === state.user.primaryInstitutionId
    );

    return primaryInstitution?.name ?? "";
  };

  const setInstitutions = (institutions: Institution[]): void => {
    state.user.institutions = institutions;
  };

  const updatePrimaryInstitution = async (institutionId: string) => {
    await InstitutionsService.update(institutionId);

    if (state.user.primaryInstitutionId !== institutionId) {
      // Re-fetch user to update dictionary licenses
      await loadUser();
    }
  };

  return {
    ...toRefs(state),

    userDisplayName,
    userIsTeacher,
    isLoaded,

    loadUser,
    login,
    logout,
    resetAuth,
    getPrimaryInstitutionName,
    setInstitutions,
    updatePrimaryInstitution
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
